/*
 * BRAW Explorer 2 product page: shared state engine, buy path and hero
 * gallery. Ported from app/sections/product-context.tsx,
 * use-sticky-cart-visibility.ts, media.tsx (VideoLoop / useVideoFallback),
 * Hero.tsx (HeroGallery), UpsellSheetShell.tsx and ExplorerUpsellSheet.tsx.
 *
 * One IIFE, exposed as window.BRAW. Prices are integer pence everywhere.
 * Sections B and C hook the `braw:change` event (detail = state + derived)
 * and the shared `[data-braw-video]` observer; they never touch state
 * directly except through BRAW.* methods or `[data-braw-action]` buttons.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- */
  /* Static product data (lib/product-data.ts). Prices in pence.       */
  /* ---------------------------------------------------------------- */

  var PRODUCT_NAME = 'BRAW Explorer 2';
  var DEFAULT_COLOR_ID = 'black-orange';

  var COLORS = [
    { id: 'black-orange', label: 'Black + Orange', price: 11500, compareAt: 13500 },
    { id: 'blue-orange', label: 'Blue + Orange', price: 11500, compareAt: 13500 },
    { id: 'blue-stripes', label: 'Blue + Stripes', price: 11500, compareAt: 13500 }
  ];

  var ADD_ONS = [
    { id: 'wee-pump', name: 'WEE Pump', price: 1999 },
    { id: 'wash-bag', name: 'Wash Bag', price: 2199 }
  ];

  var WASH_BAG_COLORS = [
    { id: 'black-orange', label: 'Black + Orange' },
    { id: 'blue-stripes', label: 'Blue + Stripes' }
  ];

  var TWO_KITS_PRICE = 25000;
  var TWO_KITS_COMPARE_AT = 27000;
  var KIT_PRICE_FALLBACK = 13500;
  var KIT_COMPARE_AT_FALLBACK = 19498;

  /* ---------------------------------------------------------------- */
  /* Helpers                                                           */
  /* ---------------------------------------------------------------- */

  function activeCurrencyCode() {
    var meta = document.querySelector('meta[property="og:price:currency"]');
    return (meta && meta.getAttribute('content')) || 'GBP';
  }

  function formatPrice(pence) {
    var n = Number(pence) || 0;
    var currency = activeCurrencyCode();
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: n % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(n / 100).replace(/\u00a0/g, ' ');
    } catch (e) {
      var symbols = { GBP: '£', EUR: '€', USD: '$', CAD: 'CA$', AUD: 'A$', NZD: 'NZ$' };
      var value = n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2);
      return (symbols[currency] || currency + ' ') + value;
    }
  }

  function pounds2(pence) {
    return (Math.abs(Number(pence) || 0) / 100).toFixed(2);
  }

  function defaultWashBagColorId(colorId) {
    return colorId === 'black-orange' ? 'black-orange' : 'blue-stripes';
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hidden) {
    if (el.hidden !== hidden) el.hidden = hidden;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------------------------------------------------------------- */
  /* Catalogue (snippets/braw-catalogue.liquid)                        */
  /* ---------------------------------------------------------------- */

  function emptyProduct() {
    return { id: 0, handle: '', title: '', variants: [] };
  }

  function readCatalogue() {
    var el = document.getElementById('braw-catalogue');
    var data = null;
    if (el) {
      try { data = JSON.parse(el.textContent || '{}'); } catch (e) { data = null; }
    }
    data = data || {};
    return {
      explorer2: data.explorer2 || emptyProduct(),
      fullKit: data.fullKit || emptyProduct(),
      weePump: data.weePump || emptyProduct(),
      washBag: data.washBag || emptyProduct()
    };
  }

  function findVariant(product, value, index) {
    index = index || 0;
    var vs = (product && product.variants) || [];
    for (var i = 0; i < vs.length; i++) {
      var ov = vs[i].optionValues || [];
      if (ov[index] === value) return vs[i];
    }
    return null;
  }

  function findKitVariant(catalogue, explorerLabel, washBagLabel) {
    var vs = (catalogue.fullKit && catalogue.fullKit.variants) || [];
    for (var i = 0; i < vs.length; i++) {
      var ov = vs[i].optionValues || [];
      if (ov[0] === explorerLabel && ov[1] === washBagLabel) return vs[i];
    }
    return null;
  }

  function mergeExplorerColors(catalogue) {
    return COLORS.map(function (c) {
      var v = findVariant(catalogue.explorer2, c.label);
      return {
        id: c.id,
        label: c.label,
        variantId: v ? v.id : 0,
        price: v && typeof v.price === 'number' ? v.price : c.price,
        compareAt: v && typeof v.compareAtPrice === 'number' ? v.compareAtPrice : null,
        available: v ? v.available !== false : true,
        featuredImage: v && v.featuredImage ? v.featuredImage : null
      };
    });
  }

  function mergeWashBagColors(catalogue) {
    return WASH_BAG_COLORS.map(function (c) {
      var v = findVariant(catalogue.washBag, c.label);
      return {
        id: c.id,
        label: c.label,
        variantId: v ? v.id : 0,
        price: v && typeof v.price === 'number' ? v.price : 2199,
        compareAt: v && typeof v.compareAtPrice === 'number' ? v.compareAtPrice : 2999,
        available: v ? v.available !== false : true
      };
    });
  }

  function mergeWeePump(catalogue) {
    var base = ADD_ONS[0];
    var v = (catalogue.weePump.variants || [])[0] || null;
    return {
      id: base.id,
      name: base.name,
      variantId: v ? v.id : 0,
      price: v && typeof v.price === 'number' ? v.price : base.price,
      compareAt: v && typeof v.compareAtPrice === 'number' ? v.compareAtPrice : null,
      available: v ? v.available !== false : true
    };
  }

  /* ---------------------------------------------------------------- */
  /* State (product-context.tsx)                                       */
  /* ---------------------------------------------------------------- */

  var catalogue = readCatalogue();
  var colors = mergeExplorerColors(catalogue);
  var washBagColors = mergeWashBagColors(catalogue);
  var weePump = mergeWeePump(catalogue);

  var state = {
    colorId: DEFAULT_COLOR_ID,
    bundle: 'single',
    addOnIds: new Set(),
    washBagColorId: defaultWashBagColorId(DEFAULT_COLOR_ID),
    washBagColorTouched: false,
    colorId2: DEFAULT_COLOR_ID,
    washBagColorId2: defaultWashBagColorId(DEFAULT_COLOR_ID),
    washBagColorTouched2: false,
    upsellOpen: false,
    pendingCheckout: false,
    sheetAddOnIds: new Set(),
    switchNotice: null,
    added: false,
    busy: false,
    cartError: null
  };

  var addedTimer = null;
  var noticeTimer = null;
  var checkoutInFlight = false;
  var upsellShownInFlight = false;

  function findById(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return list[0];
  }

  function derive() {
    var selectedColor = findById(colors, state.colorId);
    var selectedWashBagColor = findById(washBagColors, state.washBagColorId);
    var selectedColor2 = findById(colors, state.colorId2);
    var selectedWashBagColor2 = findById(washBagColors, state.washBagColorId2);

    var kitVariant = findKitVariant(catalogue, selectedColor.label, selectedWashBagColor.label);
    var kitVariant2 = findKitVariant(catalogue, selectedColor2.label, selectedWashBagColor2.label);
    var kitPrice = kitVariant && typeof kitVariant.price === 'number' ? kitVariant.price : KIT_PRICE_FALLBACK;
    var kitCompareAt = kitVariant && typeof kitVariant.compareAtPrice === 'number' ? kitVariant.compareAtPrice : KIT_COMPARE_AT_FALLBACK;

    var singlePrice = selectedColor.price;
    var singleCompareAt = selectedColor.compareAt;
    var kitSavings = selectedColor.price + weePump.price + selectedWashBagColor.price - kitPrice;

    var hasPump = state.addOnIds.has('wee-pump');
    var hasBag = state.addOnIds.has('wash-bag');
    var addOnSum = (hasPump ? weePump.price : 0) + (hasBag ? selectedWashBagColor.price : 0);
    var totalPrice = state.bundle === 'full-kit' ? kitPrice : state.bundle === 'two-kits' ? TWO_KITS_PRICE : singlePrice + addOnSum;

    var isKit = state.bundle === 'full-kit';
    var isKitLike = state.bundle !== 'single';

    // Hero / sticky bar compare-at: sum every line in the selection.
    var compareAt = state.bundle === 'two-kits'
      ? TWO_KITS_COMPARE_AT
      : isKit
        ? kitCompareAt
        : singleCompareAt +
          (hasPump ? (weePump.compareAt != null ? weePump.compareAt : weePump.price) : 0) +
          (hasBag ? selectedWashBagColor.compareAt : 0);

    var showWashBagColorPicker = state.bundle === 'full-kit' || hasBag;

    var addOnNudge = null;
    if (state.bundle === 'single' && state.addOnIds.size === 1) {
      var missing = null;
      for (var i = 0; i < ADD_ONS.length; i++) if (!state.addOnIds.has(ADD_ONS[i].id)) missing = ADD_ONS[i];
      if (missing) {
        var diff = kitPrice - totalPrice;
        if (diff > 0) {
          addOnNudge = 'Add the ' + missing.name + ' and get all three as the Full BRAW Kit for ' + formatPrice(kitPrice) + ', just ' + diff + 'p more.';
        } else if (diff === 0) {
          addOnNudge = 'Add the ' + missing.name + ' and get all three as the Full BRAW Kit for ' + formatPrice(kitPrice) + ', at no extra cost.';
        } else {
          addOnNudge = 'Add the ' + missing.name + ' and get all three as the Full BRAW Kit for ' + formatPrice(kitPrice) + ', that is ' + formatPrice(diff) + ' less than this.';
        }
      }
    }

    var selectionLabel;
    if (state.bundle === 'two-kits') selectionLabel = 'Two Full BRAW Kits';
    else if (state.bundle === 'full-kit') selectionLabel = 'Full BRAW Kit: Explorer 2 + WEE Pump + Wash Bag';
    else if (state.addOnIds.size === 0) selectionLabel = 'The Explorer 2 on its own.';
    else {
      var names = [];
      for (var j = 0; j < ADD_ONS.length; j++) if (state.addOnIds.has(ADD_ONS[j].id)) names.push(ADD_ONS[j].name);
      selectionLabel = 'Explorer 2 + ' + names.join(' + ');
    }

    var colorName = selectedColor.id === 'black-orange' ? 'black' : 'blue';
    var galleryKey = isKit ? 'kit' : selectedColor.id;

    // Upsell sheet derived values (ExplorerUpsellSheet.tsx).
    var alreadyHasAddOn = state.addOnIds.size === 1;
    var missingAddOnId = alreadyHasAddOn ? (hasPump ? 'wash-bag' : 'wee-pump') : null;
    var missingAddOnName = missingAddOnId === 'wee-pump' ? 'WEE Pump' : 'Wash Bag';
    var kitDiff = kitPrice - totalPrice;
    var kitDiffLabel = kitDiff > 0
      ? 'you pay ' + formatPrice(kitDiff) + ' more'
      : kitDiff === 0
        ? 'at no extra cost'
        : 'you pay ' + formatPrice(Math.abs(kitDiff)) + ' less than buying it alone';

    return {
      selectedColor: selectedColor,
      selectedWashBagColor: selectedWashBagColor,
      selectedColor2: selectedColor2,
      selectedWashBagColor2: selectedWashBagColor2,
      kitVariant: kitVariant,
      kitVariant2: kitVariant2,
      kitVariantAvailable: kitVariant != null,
      kitPrice: kitPrice,
      kitCompareAt: kitCompareAt,
      kitSavings: kitSavings,
      singlePrice: singlePrice,
      singleCompareAt: singleCompareAt,
      totalPrice: totalPrice,
      compareAt: compareAt,
      twoKitsPrice: TWO_KITS_PRICE,
      twoKitsCompareAt: TWO_KITS_COMPARE_AT,
      isKit: isKit,
      isKitLike: isKitLike,
      showWashBagColorPicker: showWashBagColorPicker,
      addOnNudge: addOnNudge,
      selectionLabel: selectionLabel,
      colorName: colorName,
      frontImage: selectedColor.featuredImage || null,
      galleryKey: galleryKey,
      alreadyHasAddOn: alreadyHasAddOn,
      missingAddOnId: missingAddOnId,
      missingAddOnName: missingAddOnName,
      kitDiff: kitDiff,
      kitDiffLabel: kitDiffLabel,
      weePump: weePump,
      colors: colors,
      washBagColors: washBagColors
    };
  }

  /* ---------------------------------------------------------------- */
  /* State mutations                                                   */
  /* ---------------------------------------------------------------- */

  function setColorId(id) {
    if (!findById(colors, id) || findById(colors, id).id !== id) return;
    state.colorId = id;
    if (!state.washBagColorTouched) state.washBagColorId = defaultWashBagColorId(id);
    state.pendingCheckout = false;
    render();
  }

  function setColorId2(id) {
    if (findById(colors, id).id !== id) return;
    state.colorId2 = id;
    if (!state.washBagColorTouched2) state.washBagColorId2 = defaultWashBagColorId(id);
    state.pendingCheckout = false;
    render();
  }

  function setWashBagColorId(id) {
    if (findById(washBagColors, id).id !== id) return;
    state.washBagColorTouched = true;
    state.washBagColorId = id;
    state.pendingCheckout = false;
    render();
  }

  function setWashBagColorId2(id) {
    if (findById(washBagColors, id).id !== id) return;
    state.washBagColorTouched2 = true;
    state.washBagColorId2 = id;
    state.pendingCheckout = false;
    render();
  }

  function setBundle(next) {
    if (next !== 'single' && next !== 'full-kit' && next !== 'two-kits') return;
    state.bundle = next;
    state.addOnIds = new Set();
    state.switchNotice = null;
    state.pendingCheckout = false;
    render();
  }

  function toggleAddOn(id) {
    if (state.bundle !== 'single') return;
    if (id !== 'wee-pump' && id !== 'wash-bag') return;
    state.pendingCheckout = false;
    var next = new Set(state.addOnIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    var all = ADD_ONS.every(function (a) { return next.has(a.id); });
    if (all) {
      var d = derive();
      state.bundle = 'full-kit';
      state.addOnIds = new Set();
      if (noticeTimer) clearTimeout(noticeTimer);
      state.switchNotice = 'Switched to the Full BRAW Kit. You saved ' + formatPrice(d.kitSavings) + '.';
      noticeTimer = setTimeout(function () { state.switchNotice = null; render(); }, 5000);
    } else {
      state.switchNotice = null;
      state.addOnIds = next;
    }
    render();
  }

  function toggleSheetAddOn(id) {
    var next = new Set(state.sheetAddOnIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    state.sheetAddOnIds = next;
    render();
  }

  /* ---------------------------------------------------------------- */
  /* Cart / checkout                                                   */
  /* ---------------------------------------------------------------- */

  var CART_ERROR = 'Something went wrong adding to your cart. Please try again.';

  function checkout(lines) {
    lines = (lines || []).filter(function (l) { return l && l.id > 0 && l.quantity > 0; });
    if (lines.length === 0) {
      checkoutInFlight = false;
      state.busy = false;
      render();
      return Promise.resolve(false);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart add failed ' + res.status);
      state.busy = false;
      state.added = true;
      if (addedTimer) clearTimeout(addedTimer);
      addedTimer = setTimeout(function () { state.added = false; render(); }, 2200);
      render();
      window.location.assign('/checkout');
      return true;
    }).catch(function () {
      checkoutInFlight = false;
      state.busy = false;
      state.added = false;
      state.cartError = CART_ERROR;
      render();
      return false;
    });
  }

  function finalizeCheckout(lines) {
    if (checkoutInFlight) return;
    checkoutInFlight = true;
    upsellShownInFlight = false;
    state.cartError = null;
    state.busy = true;
    state.pendingCheckout = false;
    if (state.upsellOpen) {
      state.upsellOpen = false;
      closeSheet(false);
    }
    render();
    checkout(lines);
  }

  function buildLines() {
    var d = derive();
    var lines = [];
    if (state.bundle === 'full-kit') {
      if (d.kitVariant) lines.push({ id: d.kitVariant.id, quantity: 1 });
    } else if (state.bundle === 'two-kits') {
      if (d.kitVariant && d.kitVariant2) {
        if (d.kitVariant.id === d.kitVariant2.id) {
          lines.push({ id: d.kitVariant.id, quantity: 2 });
        } else {
          lines.push({ id: d.kitVariant.id, quantity: 1 });
          lines.push({ id: d.kitVariant2.id, quantity: 1 });
        }
      }
    } else {
      lines.push({ id: d.selectedColor.variantId, quantity: 1 });
      if (state.addOnIds.has('wee-pump')) lines.push({ id: weePump.variantId, quantity: 1 });
      if (state.addOnIds.has('wash-bag')) lines.push({ id: d.selectedWashBagColor.variantId, quantity: 1 });
    }
    return lines;
  }

  function checkoutCurrentSelection() {
    finalizeCheckout(buildLines());
  }

  function addToCart() {
    if (state.busy || checkoutInFlight) return;
    var d = derive();
    if (state.bundle === 'single' && !d.selectedColor.available) return;
    if (state.bundle === 'full-kit' || state.bundle === 'two-kits' || state.pendingCheckout) {
      checkoutCurrentSelection();
      return;
    }
    if (upsellShownInFlight) return;
    upsellShownInFlight = true;
    state.sheetAddOnIds = new Set();
    state.cartError = null;
    state.upsellOpen = true;
    render();
    openSheet();
  }

  function confirmUpgradeToKit() {
    var d = derive();
    if (!d.kitVariant) return;
    finalizeCheckout([{ id: d.kitVariant.id, quantity: 1 }]);
  }

  function confirmAddWeePumpOnly() {
    var d = derive();
    finalizeCheckout([
      { id: d.selectedColor.variantId, quantity: 1 },
      { id: weePump.variantId, quantity: 1 }
    ]);
  }

  function confirmAddWashBagOnly() {
    var d = derive();
    finalizeCheckout([
      { id: d.selectedColor.variantId, quantity: 1 },
      { id: d.selectedWashBagColor.variantId, quantity: 1 }
    ]);
  }

  function declineUpsell() {
    checkoutCurrentSelection();
  }

  function dismissUpsell() {
    state.upsellOpen = false;
    state.pendingCheckout = true;
    upsellShownInFlight = false;
    closeSheet(true);
    render();
  }

  /* ---------------------------------------------------------------- */
  /* Upsell sheet dialog (UpsellSheetShell.tsx)                        */
  /* ---------------------------------------------------------------- */

  var sheetRoot = null;
  var sheetPopup = null;
  var sheetLastFocus = null;
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openSheet() {
    sheetRoot = document.getElementById('braw-upsell-sheet');
    if (!sheetRoot) {
      // No sheet on the page: behave like "No thanks", go straight to checkout.
      upsellShownInFlight = false;
      state.upsellOpen = false;
      checkoutCurrentSelection();
      return;
    }
    sheetPopup = sheetRoot.querySelector('[data-braw-sheet-popup]');
    sheetLastFocus = document.activeElement;
    sheetRoot.hidden = false;
    sheetRoot.classList.add('is-open');
    document.documentElement.classList.add('braw-sheet-open');
    document.body.style.overflow = 'hidden';
    var first = sheetPopup ? sheetPopup.querySelector('[data-braw-sheet-close]') || sheetPopup.querySelector(FOCUSABLE) : null;
    if (first) {
      window.setTimeout(function () { try { first.focus(); } catch (e) {} }, 20);
    }
  }

  function closeSheet(restoreFocus) {
    sheetRoot = sheetRoot || document.getElementById('braw-upsell-sheet');
    if (!sheetRoot) return;
    sheetRoot.classList.remove('is-open');
    sheetRoot.hidden = true;
    document.documentElement.classList.remove('braw-sheet-open');
    document.body.style.overflow = '';
    if (restoreFocus && sheetLastFocus && typeof sheetLastFocus.focus === 'function') {
      try { sheetLastFocus.focus(); } catch (e) {}
    }
    sheetLastFocus = null;
  }

  function onSheetKeydown(e) {
    if (!state.upsellOpen) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      dismissUpsell();
      return;
    }
    if (e.key !== 'Tab' || !sheetPopup) return;
    var focusable = qsa(FOCUSABLE, sheetPopup).filter(function (el) {
      return !el.hidden && el.offsetParent !== null;
    });
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !sheetPopup.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !sheetPopup.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------- */
  /* Rendering                                                         */
  /* ---------------------------------------------------------------- */

  function swatchRender(sel, currentId, list) {
    qsa(sel).forEach(function (btn) {
      var id = btn.getAttribute('data-color');
      var on = id === currentId;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-selected', on);
      var c = null;
      for (var i = 0; i < list.length; i++) if (list[i].id === id) c = list[i];
      if (c) {
        var available = c.available !== false;
        btn.disabled = !available;
        btn.classList.toggle('is-sold-out', !available);
        // Hero / Complete your kit swatches carry the price in the label
        // (`data-braw-price-label`); Bundle and Save swatches do not.
        var withPrice = btn.hasAttribute('data-braw-price-label');
        btn.setAttribute('aria-label', available ? (withPrice ? c.label + ', ' + formatPrice(c.price) : c.label) : c.label + ', sold out');
      }
    });
  }

  function buyLabel(d, short) {
    if (state.busy) return 'Adding...';
    if (state.added) return short ? 'Added' : 'Added to cart';
    if (state.pendingCheckout) return 'Go to checkout';
    if (short) return 'Add to cart';
    if (state.bundle === 'single' && !d.selectedColor.available) return 'Sold out';
    if (state.bundle === 'full-kit') return 'Add Full BRAW Kit, ' + formatPrice(d.totalPrice);
    if (state.bundle === 'two-kits') return 'Add two Full BRAW Kits, ' + formatPrice(d.totalPrice);
    return 'Add Explorer 2, ' + formatPrice(d.totalPrice);
  }

  function showCondition(cond, d) {
    switch (cond) {
      case 'single': return state.bundle === 'single';
      case 'full-kit': return state.bundle === 'full-kit';
      case 'two-kits': return state.bundle === 'two-kits';
      case 'kit-like': return d.isKitLike;
      case 'not-kit-like': return !d.isKitLike;
      case 'no-addons': return state.addOnIds.size === 0;
      case 'single-no-addons': return state.bundle === 'single' && state.addOnIds.size === 0;
      case 'has-pump': return state.addOnIds.has('wee-pump');
      case 'has-bag': return state.addOnIds.has('wash-bag');
      case 'washbag-picker': return d.showWashBagColorPicker;
      case 'kit-estimate': return d.isKitLike && !d.kitVariantAvailable;
      case 'switch-notice': return !!state.switchNotice;
      case 'addon-nudge': return !!d.addOnNudge;
      case 'pending': return state.pendingCheckout;
      case 'added': return state.added;
      case 'busy': return state.busy;
      case 'sold-out': return !d.selectedColor.available;
      case 'available': return d.selectedColor.available;
      case 'compare': return d.compareAt > d.totalPrice;
      case 'sheet-already-has-addon': return d.alreadyHasAddOn && !!d.missingAddOnId;
      case 'sheet-fresh': return !(d.alreadyHasAddOn && !!d.missingAddOnId);
      case 'sheet-pump': return state.sheetAddOnIds.has('wee-pump');
      case 'sheet-bag': return state.sheetAddOnIds.has('wash-bag');
      case 'sheet-both': return state.sheetAddOnIds.size === 2;
      case 'sheet-one': return state.sheetAddOnIds.size === 1;
      case 'sheet-one-pump': return state.sheetAddOnIds.size === 1 && state.sheetAddOnIds.has('wee-pump');
      case 'sheet-one-bag': return state.sheetAddOnIds.size === 1 && state.sheetAddOnIds.has('wash-bag');
      case 'cart-error': return !!state.cartError;
      default: return true;
    }
  }

  function render() {
    var d = derive();

    // Root data attributes for CSS hooks in every section.
    document.documentElement.setAttribute('data-braw-bundle', state.bundle);
    qsa('.braw-pdp').forEach(function (root) {
      root.setAttribute('data-braw-bundle', state.bundle);
      root.setAttribute('data-braw-color', state.colorId);
    });

    // Text hooks.
    qsa('[data-braw-price]').forEach(function (el) { setText(el, formatPrice(d.totalPrice)); });
    qsa('[data-braw-compare]').forEach(function (el) {
      var show = d.compareAt > d.totalPrice;
      setHidden(el, !show);
      var rrp = el.getAttribute('data-braw-compare') === 'rrp' && d.isKitLike;
      setText(el, (rrp ? 'RRP ' : '') + formatPrice(d.compareAt));
    });
    qsa('[data-braw-selection-label]').forEach(function (el) { setText(el, d.selectionLabel); });
    qsa('[data-braw-color-name]').forEach(function (el) { setText(el, d.selectedColor.label); });
    qsa('[data-braw-color2-name]').forEach(function (el) { setText(el, d.selectedColor2.label); });
    qsa('[data-braw-washbag-color-name]').forEach(function (el) { setText(el, d.selectedWashBagColor.label); });
    qsa('[data-braw-washbag-color2-name]').forEach(function (el) { setText(el, d.selectedWashBagColor2.label); });
    qsa('[data-braw-single-price]').forEach(function (el) { setText(el, formatPrice(d.singlePrice)); });
    qsa('[data-braw-kit-price]').forEach(function (el) { setText(el, formatPrice(d.kitPrice)); });
    qsa('[data-braw-kit-savings]').forEach(function (el) { setText(el, formatPrice(d.kitSavings)); });
    qsa('[data-braw-kit-savings-2dp]').forEach(function (el) { setText(el, formatPrice(d.kitSavings)); });
    qsa('[data-braw-two-kits-price]').forEach(function (el) { setText(el, formatPrice(d.twoKitsPrice)); });
    qsa('[data-braw-two-kits-compare]').forEach(function (el) { setText(el, formatPrice(d.twoKitsCompareAt)); });
    qsa('[data-braw-pump-price]').forEach(function (el) { setText(el, formatPrice(weePump.price)); });
    qsa('[data-braw-bag-price]').forEach(function (el) { setText(el, formatPrice(d.selectedWashBagColor.price)); });
    qsa('[data-braw-single-plus-pump]').forEach(function (el) { setText(el, formatPrice(d.singlePrice + weePump.price)); });
    qsa('[data-braw-single-plus-bag]').forEach(function (el) { setText(el, formatPrice(d.singlePrice + d.selectedWashBagColor.price)); });
    qsa('[data-braw-separately]').forEach(function (el) { setText(el, formatPrice(d.singlePrice + weePump.price + d.selectedWashBagColor.price)); });
    qsa('[data-braw-sold-out-name]').forEach(function (el) { setText(el, d.selectedColor.label); });
    qsa('[data-braw-sticky-price-line]').forEach(function (el) {
      var line = state.bundle === 'two-kits'
        ? formatPrice(d.totalPrice) + ' · Two Kits'
        : d.isKit
          ? formatPrice(d.totalPrice) + ' · Full Kit'
          : formatPrice(d.totalPrice);
      setText(el, line);
    });
    qsa('[data-braw-switch-notice]').forEach(function (el) {
      setHidden(el, !state.switchNotice);
      setText(el, state.switchNotice || '');
    });
    qsa('[data-braw-addon-nudge]').forEach(function (el) {
      setHidden(el, !d.addOnNudge);
      setText(el, d.addOnNudge || '');
    });
    qsa('[data-braw-cart-error]').forEach(function (el) {
      setHidden(el, !state.cartError);
      setText(el, state.cartError || '');
    });

    // Sheet text hooks.
    qsa('[data-braw-sheet-title]').forEach(function (el) { setText(el, 'Added: Explorer 2, ' + formatPrice(d.singlePrice)); });
    qsa('[data-braw-missing-addon-name]').forEach(function (el) { setText(el, d.missingAddOnName); });
    qsa('[data-braw-kit-diff-label]').forEach(function (el) { setText(el, d.kitDiffLabel); });
    qsa('[data-braw-sheet-toggle][data-addon]').forEach(function (el) {
      var on = state.sheetAddOnIds.has(el.getAttribute('data-addon'));
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.classList.toggle('is-on', on);
      var mark = el.querySelector('[data-braw-sheet-mark]');
      if (mark) setText(mark, on ? '✓' : '+');
    });
    qsa('[data-braw-sheet-washbag-chip][data-color]').forEach(function (el) {
      var on = el.getAttribute('data-color') === state.washBagColorId;
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.classList.toggle('is-selected', on);
    });

    // Conditional blocks.
    qsa('[data-braw-show]').forEach(function (el) {
      var conds = (el.getAttribute('data-braw-show') || '').split(/\s+/).filter(Boolean);
      var show = conds.length === 0;
      for (var i = 0; i < conds.length; i++) if (showCondition(conds[i], d)) { show = true; break; }
      setHidden(el, !show);
    });
    qsa('[data-braw-washbag-picker]').forEach(function (el) { setHidden(el, !d.showWashBagColorPicker); });
    qsa('[data-braw-kit2]').forEach(function (el) { setHidden(el, state.bundle !== 'two-kits'); });

    // Swatches.
    swatchRender('[data-braw-swatch][data-color]', state.colorId, colors);
    swatchRender('[data-braw-swatch2][data-color]', state.colorId2, colors);
    swatchRender('[data-braw-washbag-swatch][data-color]', state.washBagColorId, washBagColors);
    swatchRender('[data-braw-washbag-swatch2][data-color]', state.washBagColorId2, washBagColors);

    // Bundle options.
    qsa('[data-braw-bundle-option][data-bundle]').forEach(function (el) {
      var on = el.getAttribute('data-bundle') === state.bundle;
      if (el.getAttribute('role') === 'radio') el.setAttribute('aria-checked', on ? 'true' : 'false');
      el.classList.toggle('is-selected', on);
    });

    // Add-on toggles (CompleteYourKit, TheTrip, ProductExplorer).
    qsa('[data-braw-addon][data-addon]').forEach(function (el) {
      var id = el.getAttribute('data-addon');
      var soldOut = id === 'wee-pump' ? !weePump.available : !d.selectedWashBagColor.available;
      var on = d.isKitLike || state.addOnIds.has(id);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.classList.toggle('is-on', on);
      el.classList.toggle('is-sold-out', soldOut);
      el.classList.toggle('is-kit', d.isKitLike);
      if (el.tagName === 'BUTTON') el.disabled = d.isKitLike || soldOut;
    });

    // Buy buttons.
    qsa('[data-braw-buy]').forEach(function (el) {
      var heroLike = el.getAttribute('data-braw-buy') === 'hero';
      var disabled = state.busy || (heroLike && state.bundle === 'single' && !d.selectedColor.available);
      el.disabled = disabled;
      el.classList.toggle('is-added', state.added);
      el.classList.toggle('is-busy', state.busy);
      el.setAttribute('aria-busy', state.busy ? 'true' : 'false');
    });
    qsa('[data-braw-buy-label]').forEach(function (el) {
      var short = el.getAttribute('data-braw-buy-label') === 'short';
      setText(el, buyLabel(d, short));
    });
    qsa('[data-braw-buy-check]').forEach(function (el) { setHidden(el, !state.added); });

    // Hero gallery.
    galleryRender(d);

    // Sticky bar re-check (button label height may change).
    stickyCheck();

    var detail = {};
    for (var k in state) if (Object.prototype.hasOwnProperty.call(state, k)) detail[k] = state[k];
    for (var k2 in d) if (Object.prototype.hasOwnProperty.call(d, k2)) detail[k2] = d[k2];
    detail.formatPrice = formatPrice;
    document.dispatchEvent(new CustomEvent('braw:change', { detail: detail }));
  }

  /* ---------------------------------------------------------------- */
  /* Video observer (media.tsx VideoLoop / useVideoFallback)           */
  /* Shared by every section: any <video data-braw-video> plays when   */
  /* at least 25% visible, pauses when out, and hides itself (showing  */
  /* the still / poster underneath) on error, no data within 3s of the */
  /* first play attempt, or a stall (no progress within 1.5s).         */
  /* ---------------------------------------------------------------- */

  var videoObserver = null;
  var observedVideos = new WeakSet();

  function failVideo(video) {
    if (video.dataset.brawFailed === '1') return;
    video.dataset.brawFailed = '1';
    try { video.pause(); } catch (e) {}
    video.classList.add('is-failed');
    video.hidden = true;
    var wrap = video.closest('[data-braw-media]');
    if (wrap) wrap.classList.add('is-video-failed');
    video.dispatchEvent(new CustomEvent('braw:videofail', { bubbles: true }));
  }

  function armVideoWatchdogs(video) {
    if (video.dataset.brawArmed === '1') return;
    video.dataset.brawArmed = '1';
    window.setTimeout(function () {
      if (video.dataset.brawFailed === '1') return;
      if (video.readyState === 0) failVideo(video);
    }, 3000);
    var start = video.currentTime;
    window.setTimeout(function () {
      if (video.dataset.brawFailed === '1') return;
      if (video.ended) return;
      // Only judge a stall if the clip is still meant to be playing.
      if (video.dataset.brawInView !== '1') return;
      var advanced = video.currentTime - start >= 0.25;
      if (!advanced || video.paused) failVideo(video);
    }, 1500);
  }

  function playVideo(video) {
    if (video.dataset.brawFailed === '1') return;
    if (reducedMotion()) return;
    video.muted = true;
    video.defaultMuted = true;
    if (video.getAttribute('preload') === 'none') video.setAttribute('preload', 'auto');
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
    armVideoWatchdogs(video);
  }

  function observeVideo(video) {
    if (observedVideos.has(video)) return;
    observedVideos.add(video);
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('playsinline', '');
    video.addEventListener('error', function () { failVideo(video); });
    if (video.dataset.brawVideo === 'manual') return; // section drives play() itself
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            v.dataset.brawInView = '1';
            playVideo(v);
          } else {
            v.dataset.brawInView = '0';
            try { v.pause(); } catch (e) {}
          }
        });
      }, { threshold: [0, 0.25, 0.5] });
    }
    videoObserver.observe(video);
  }

  function scanVideos(root) {
    qsa('video[data-braw-video]', root || document).forEach(observeVideo);
  }

  /* ---------------------------------------------------------------- */
  /* Sticky bar visibility (use-sticky-cart-visibility.ts)             */
  /* ---------------------------------------------------------------- */

  var stickyBar = null;
  var stickyButtonBottom = Infinity;
  var stickyButtonTop = -Infinity;
  var stickyScheduled = false;
  var stickyVisible = false;
  var STICKY_ANCHOR_ID = 'braw-buy-box';

  function stickyMeasure() {
    var el = document.getElementById(STICKY_ANCHOR_ID);
    if (!el) { stickyButtonBottom = Infinity; stickyButtonTop = -Infinity; return; }
    var btn = el.querySelector('[data-braw-buy]') || el;
    var r = btn.getBoundingClientRect();
    stickyButtonTop = r.top + window.scrollY;
    stickyButtonBottom = r.bottom + window.scrollY;
  }

  function stickyApply() {
    if (!stickyBar) return;
    var buyInView = stickyButtonTop >= window.scrollY && stickyButtonBottom <= window.scrollY + window.innerHeight;
    var visible = !buyInView && !state.upsellOpen;
    if (visible !== stickyVisible) {
      stickyVisible = visible;
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    if (visible) {
      var h = stickyBar.getBoundingClientRect().height;
      document.body.style.paddingBottom = h + 'px';
    } else {
      document.body.style.paddingBottom = '';
    }
  }

  function stickyCheck() {
    stickyScheduled = false;
    stickyApply();
  }

  function onStickyScroll() {
    if (stickyScheduled) return;
    stickyScheduled = true;
    window.requestAnimationFrame(stickyCheck);
  }

  function onStickyResize() {
    stickyMeasure();
    onStickyScroll();
  }

  function initSticky() {
    stickyBar = document.getElementById('braw-sticky-bar');
    if (!stickyBar) return;
    stickyMeasure();
    stickyCheck();
    window.addEventListener('scroll', onStickyScroll, { passive: true });
    window.addEventListener('resize', onStickyResize);
    window.addEventListener('load', onStickyResize);
  }

  /* ---------------------------------------------------------------- */
  /* Hero gallery carousel (Hero.tsx HeroGallery)                      */
  /* ---------------------------------------------------------------- */

  var gallery = {
    root: null,
    track: null,
    dots: null,
    thumbs: null,
    caption: null,
    galleries: null,
    key: null,
    selected: 0,
    slides: [],
    scrollTimer: null,
    suppressScroll: false
  };

  function galleryReadData() {
    if (gallery.galleries) return gallery.galleries;
    var el = gallery.root && gallery.root.querySelector('[data-braw-galleries]');
    if (!el) return null;
    try { gallery.galleries = JSON.parse(el.textContent || '{}'); } catch (e) { gallery.galleries = null; }
    return gallery.galleries;
  }

  function slidesFor(key, d) {
    var data = galleryReadData();
    if (!data) return null;
    var list = data[key] || data['black-orange'] || null;
    if (!list) return null;
    list = list.slice();
    // Colourways without their own photography (black-orange) use the
    // variant's Shopify featured image as the front shot when there is one
    // (Hero.tsx: frontImage = selectedColor.featuredImage ?? galleryImage).
    if (key !== 'kit' && !(data.own && data.own.indexOf(key) !== -1) && d.frontImage && list[0] && !list[0].isVideo) {
      list[0] = { src: d.frontImage, alt: list[0].alt, caption: list[0].caption };
    }
    return list;
  }

  function galleryBuild(key, d) {
    var slides = slidesFor(key, d);
    if (!slides || !gallery.track) return;
    var track = gallery.track;
    var html = '';
    var dotsHtml = '';
    var thumbsHtml = '';
    slides.forEach(function (s, i) {
      if (s.isVideo) {
        html += '<div class="braw-gallery__slide braw-gallery__slide--video" data-braw-slide="' + i + '" data-braw-media>' +
          '<img class="braw-gallery__img braw-gallery__still" src="' + s.poster + '" alt="" loading="lazy" decoding="async" aria-hidden="true">' +
          '<video class="braw-gallery__video" data-braw-video src="' + s.src + '" poster="' + s.poster + '" muted playsinline loop preload="none" aria-label="' + escapeAttr(s.alt) + '"></video>' +
          '<span class="braw-gallery__badge">Fits the sizer</span>' +
          '</div>';
        thumbsHtml += '<button type="button" class="braw-gallery__thumb" data-braw-thumb="' + i + '" aria-label="Show image ' + (i + 1) + '"><img src="' + s.poster + '" alt="" loading="lazy" decoding="async" width="48" height="48"></button>';
      } else {
        html += '<div class="braw-gallery__slide" data-braw-slide="' + i + '">' +
          '<img class="braw-gallery__img" src="' + s.src + '" alt="' + escapeAttr(s.alt) + '" loading="' + (i === 0 ? 'eager' : 'lazy') + '" decoding="async"' + (i === 0 ? ' fetchpriority="high"' : '') + '>' +
          '</div>';
        thumbsHtml += '<button type="button" class="braw-gallery__thumb" data-braw-thumb="' + i + '" aria-label="Show image ' + (i + 1) + '"><img src="' + s.src + '" alt="" loading="lazy" decoding="async" width="48" height="48"></button>';
      }
      dotsHtml += '<button type="button" class="braw-gallery__dot" data-braw-dot="' + i + '" aria-label="Go to image ' + (i + 1) + '"><span></span></button>';
    });
    track.innerHTML = html;
    if (gallery.dots) gallery.dots.innerHTML = dotsHtml;
    if (gallery.thumbs) gallery.thumbs.innerHTML = thumbsHtml;
    gallery.slides = slides;
    gallery.key = key;
    scanVideos(track);
    galleryGo(0, true);
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function gallerySetSelected(i) {
    gallery.selected = i;
    var slides = gallery.slides;
    if (gallery.caption) setText(gallery.caption, (slides[i] && slides[i].caption) || '');
    qsa('[data-braw-dot]', gallery.root).forEach(function (b) {
      var on = Number(b.getAttribute('data-braw-dot')) === i;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
    qsa('[data-braw-thumb]', gallery.root).forEach(function (b) {
      var on = Number(b.getAttribute('data-braw-thumb')) === i;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
    qsa('[data-braw-slide]', gallery.track).forEach(function (s) {
      var on = Number(s.getAttribute('data-braw-slide')) === i;
      s.classList.toggle('is-active', on);
      // The sizer clip only plays while its slide is the active one.
      var v = s.querySelector('video[data-braw-video]');
      if (v) {
        if (on) { v.dataset.brawInView = '1'; playVideo(v); }
        else { v.dataset.brawInView = '0'; try { v.pause(); } catch (e) {} }
      }
    });
    var prev = gallery.root.querySelector('[data-braw-gallery-prev]');
    var next = gallery.root.querySelector('[data-braw-gallery-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= slides.length - 1;
  }

  function galleryGo(i, instant) {
    if (!gallery.track) return;
    var n = gallery.slides.length;
    if (n === 0) return;
    if (i < 0) i = 0;
    if (i > n - 1) i = n - 1;
    var w = gallery.track.clientWidth;
    gallery.suppressScroll = true;
    try {
      gallery.track.scrollTo({ left: i * w, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
    } catch (e) {
      gallery.track.scrollLeft = i * w;
    }
    gallerySetSelected(i);
    window.clearTimeout(gallery.scrollTimer);
    gallery.scrollTimer = window.setTimeout(function () { gallery.suppressScroll = false; }, instant ? 50 : 500);
  }

  function onGalleryScroll() {
    if (gallery.suppressScroll) return;
    window.clearTimeout(gallery.scrollTimer);
    gallery.scrollTimer = window.setTimeout(function () {
      var w = gallery.track.clientWidth || 1;
      var i = Math.round(gallery.track.scrollLeft / w);
      if (i !== gallery.selected) gallerySetSelected(i);
    }, 80);
  }

  function initGalleryDrag() {
    // Mouse drag to swipe on desktop; touch uses native scroll-snap.
    var track = gallery.track;
    var dragging = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      dragging = true; moved = false;
      startX = e.clientX; startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    function end(e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      var w = track.clientWidth || 1;
      var dx = e.clientX - startX;
      var target = gallery.selected;
      if (dx < -40) target += 1;
      else if (dx > 40) target -= 1;
      galleryGo(target, false);
      gallery.suppressScroll = true;
      window.setTimeout(function () { gallery.suppressScroll = false; }, 500);
      void w;
    }
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); moved = false; }
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function initGallery() {
    gallery.root = document.querySelector('[data-braw-gallery]');
    if (!gallery.root) return;
    gallery.track = gallery.root.querySelector('[data-braw-gallery-track]');
    gallery.dots = gallery.root.querySelector('[data-braw-gallery-dots]');
    gallery.thumbs = gallery.root.querySelector('[data-braw-gallery-thumbs]');
    gallery.caption = gallery.root.querySelector('[data-braw-gallery-caption]');
    if (!gallery.track) return;
    var d = derive();
    // The server rendered the default gallery; adopt it without rebuilding
    // unless the state already differs (e.g. ?bundle=kit).
    var serverKey = gallery.root.getAttribute('data-braw-gallery') || 'black-orange';
    var data = galleryReadData();
    if (data && data[serverKey] && serverKey === d.galleryKey) {
      gallery.slides = slidesFor(serverKey, d);
      gallery.key = serverKey;
      scanVideos(gallery.track);
      gallerySetSelected(0);
    } else {
      galleryBuild(d.galleryKey, d);
    }
    gallery.track.addEventListener('scroll', onGalleryScroll, { passive: true });
    window.addEventListener('resize', function () { galleryGo(gallery.selected, true); });
    gallery.root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-braw-dot], [data-braw-thumb], [data-braw-gallery-prev], [data-braw-gallery-next]');
      if (!t || !gallery.root.contains(t)) return;
      if (t.hasAttribute('data-braw-dot')) galleryGo(Number(t.getAttribute('data-braw-dot')), true);
      else if (t.hasAttribute('data-braw-thumb')) galleryGo(Number(t.getAttribute('data-braw-thumb')), true);
      else if (t.hasAttribute('data-braw-gallery-prev')) galleryGo(gallery.selected - 1, false);
      else galleryGo(gallery.selected + 1, false);
    });
    gallery.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); galleryGo(gallery.selected - 1, false); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); galleryGo(gallery.selected + 1, false); }
    });
    initGalleryDrag();
  }

  function galleryRender(d) {
    if (!gallery.root || !gallery.track) return;
    if (gallery.key !== d.galleryKey) {
      galleryBuild(d.galleryKey, d);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Click delegation                                                  */
  /* ---------------------------------------------------------------- */

  function scrollToBuyBox() {
    var el = document.getElementById(STICKY_ANCHOR_ID) || document.getElementById('braw-hero');
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    } catch (e) {
      el.scrollIntoView();
    }
    window.setTimeout(function () {
      try { el.focus({ preventScroll: true }); } catch (err) { try { el.focus(); } catch (e2) {} }
    }, 350);
  }

  function onClick(e) {
    var t = e.target.closest('[data-braw-action]');
    if (!t) return;
    var action = t.getAttribute('data-braw-action');
    switch (action) {
      case 'set-color': setColorId(t.getAttribute('data-color')); break;
      case 'set-color2': setColorId2(t.getAttribute('data-color')); break;
      case 'set-washbag-color': setWashBagColorId(t.getAttribute('data-color')); break;
      case 'set-washbag-color2': setWashBagColorId2(t.getAttribute('data-color')); break;
      case 'set-bundle': setBundle(t.getAttribute('data-bundle')); break;
      case 'toggle-addon': toggleAddOn(t.getAttribute('data-addon')); break;
      case 'add-to-cart': e.preventDefault(); addToCart(); break;
      case 'scroll-to-buy': e.preventDefault(); scrollToBuyBox(); break;
      case 'select-kit-and-scroll': e.preventDefault(); setBundle('full-kit'); scrollToBuyBox(); break;
      case 'open-upsell':
        if (!state.upsellOpen) { state.sheetAddOnIds = new Set(); state.upsellOpen = true; render(); openSheet(); }
        break;
      case 'close-upsell': dismissUpsell(); break;
      case 'sheet-toggle-addon': toggleSheetAddOn(t.getAttribute('data-addon')); break;
      case 'sheet-upgrade-kit': confirmUpgradeToKit(); break;
      case 'sheet-add-pump': confirmAddWeePumpOnly(); break;
      case 'sheet-add-bag': confirmAddWashBagOnly(); break;
      case 'sheet-decline': declineUpsell(); break;
      case 'add-addon-and-scroll': {
        var id = t.getAttribute('data-addon');
        if (state.bundle === 'single' && !state.addOnIds.has(id)) toggleAddOn(id);
        scrollToBuyBox();
        break;
      }
      default: break;
    }
  }

  /* ---------------------------------------------------------------- */
  /* Init                                                              */
  /* ---------------------------------------------------------------- */

  function readInitialBundle() {
    try {
      var params = new URLSearchParams(window.location.search);
      var b = params.get('bundle');
      if (b === 'kit') return 'full-kit';
      if (b === 'two-kits') return 'two-kits';
    } catch (e) {}
    return 'single';
  }

  function init() {
    state.bundle = readInitialBundle();
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onSheetKeydown);
    var backdrop = document.querySelector('#braw-upsell-sheet [data-braw-sheet-backdrop]');
    if (backdrop) backdrop.addEventListener('click', function () { dismissUpsell(); });
    var sheet = document.getElementById('braw-upsell-sheet');
    if (sheet) {
      sheet.addEventListener('click', function (e) {
        // Tap outside the popup (on the sheet root itself) dismisses.
        if (e.target === sheet) dismissUpsell();
      });
    }
    initGallery();
    initSticky();
    scanVideos(document);
    // Late-added videos (modals built by other sections) get picked up.
    if (window.MutationObserver) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches('video[data-braw-video]')) observeVideo(n);
            else if (n.querySelectorAll) scanVideos(n);
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
    render();
    document.documentElement.classList.add('braw-js');
  }

  var BRAW = {
    get state() { return state; },
    derive: derive,
    render: render,
    formatPrice: formatPrice,
    setColorId: setColorId,
    setColorId2: setColorId2,
    setWashBagColorId: setWashBagColorId,
    setWashBagColorId2: setWashBagColorId2,
    setBundle: setBundle,
    toggleAddOn: toggleAddOn,
    addToCart: addToCart,
    checkout: checkout,
    openUpsell: function () { if (!state.upsellOpen) { state.sheetAddOnIds = new Set(); state.upsellOpen = true; render(); openSheet(); } },
    dismissUpsell: dismissUpsell,
    scrollToBuyBox: scrollToBuyBox,
    observeVideo: observeVideo,
    scanVideos: scanVideos,
    playVideo: playVideo,
    failVideo: failVideo,
    galleryGo: galleryGo,
    catalogue: catalogue,
    colors: colors,
    washBagColors: washBagColors,
    weePump: weePump
  };
  window.BRAW = BRAW;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* === B === */
/* ==========================================================================
   BRAW PDP: Agent B behaviour (appended to assets/braw-pdp.js)
   Ports: app/sections/InterruptRail.tsx, TheTrip.tsx, ProductExplorer.tsx
   and the video helpers in app/sections/media.tsx (VideoLoop,
   StillThenVideo, VideoOnceRevealMedia, useVideoFallback).
   Agent A's shared observer plays/pauses every [data-braw-video]; this
   file adds the still fallback, the TheTrip sequence, the CLIPPOD
   play-once-then-hold, the hotspot drawer clips and the rail modal.
   Reads BRAW state from the `braw:change` event (detail = state).
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var HAS_IO = 'IntersectionObserver' in window;

  function noop() {}
  function toArray(list) { return Array.prototype.slice.call(list || []); }
  function qs(root, sel) { return root ? root.querySelector(sel) : null; }
  function qsa(root, sel) { return root ? toArray(root.querySelectorAll(sel)) : []; }

  /* ---------------------------------------------------------------------
     State helpers (product-context.tsx shape: bundle, addOnIds)
     --------------------------------------------------------------------- */
  function readState(detail) {
    var s = detail;
    if (!s && window.BRAW) s = window.BRAW.state || (typeof window.BRAW.getState === 'function' ? window.BRAW.getState() : null);
    if (s && s.state && !s.bundle) s = s.state;
    s = s || {};
    var ids = s.addOnIds;
    function has(id) {
      if (!ids) return false;
      if (typeof ids.has === 'function') return ids.has(id);
      if (Array.isArray(ids)) return ids.indexOf(id) >= 0;
      if (typeof ids === 'object') return !!ids[id];
      return false;
    }
    return { bundle: s.bundle || 'single', has: has };
  }

  /* Use the shared formatter so Shopify Markets keeps the correct currency. */
  function formatPrice(pence) {
    if (window.BRAW && typeof window.BRAW.formatPrice === 'function') return window.BRAW.formatPrice(pence);
    var n = Number(pence) || 0;
    var meta = document.querySelector('meta[property="og:price:currency"]');
    var currency = (meta && meta.getAttribute('content')) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: n % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
      }).format(n / 100).replace(/\u00a0/g, ' ');
    } catch (e) {
      return currency + ' ' + (n / 100).toFixed(n % 100 === 0 ? 0 : 2);
    }
  }

  function readCatalogue() {
    var el = document.getElementById('braw-catalogue');
    if (!el) return null;
    try { return JSON.parse(el.textContent || ''); } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------------
     Video still fallback (media.tsx useVideoFallback)
     error -> failed; no data within 3s of arming -> failed; after a play
     attempt currentTime must advance by 0.25s within 1500ms and the element
     must not sit paused while still in view -> otherwise failed.
     On failure the video element is removed and the still underneath shows.
     --------------------------------------------------------------------- */
  function createFallback(video, onFail) {
    var state = { failed: false, inView: false };
    var timers = [];
    function clearTimers() {
      while (timers.length) clearTimeout(timers.pop());
    }
    function fail() {
      if (state.failed) return;
      state.failed = true;
      clearTimers();
      try { video.pause(); } catch (e) { /* ignore */ }
      if (video.parentNode) video.parentNode.removeChild(video);
      onFail();
    }
    video.addEventListener('error', fail);
    qsa(video, 'source').forEach(function (s) { s.addEventListener('error', fail); });
    state.arm = function (withStall) {
      if (state.failed) return;
      clearTimers();
      timers.push(setTimeout(function () {
        if (video.readyState === 0) fail();
      }, 3000));
      if (withStall) {
        var start = video.currentTime;
        timers.push(setTimeout(function () {
          if (video.ended || !state.inView) return;
          var advanced = video.currentTime - start >= 0.25;
          if (!advanced || video.paused) fail();
        }, 1500));
      }
    };
    state.disarm = clearTimers;
    state.fail = fail;
    return state;
  }

  /* A muted looping clip over a still (media.tsx VideoLoop). Plays in view,
     pauses out of view, falls back to the still on failure. */
  function initLoopVideo(video) {
    if (!video || video.__brawLoop) return;
    video.__brawLoop = true;
    if (REDUCED_MOTION) {
      if (video.parentNode) video.parentNode.removeChild(video);
      return;
    }
    video.muted = true;
    video.defaultMuted = true;
    var fb = createFallback(video, noop);
    function enter() {
      fb.inView = true;
      video.preload = 'auto';
      video.muted = true;
      fb.arm(true);
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(noop);
    }
    function leave() {
      fb.inView = false;
      fb.disarm();
      try { video.pause(); } catch (e) { /* ignore */ }
    }
    if (!HAS_IO) { enter(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) enter(); else leave();
      });
    }, { threshold: 0.25 });
    io.observe(video);
    video.__brawLoopStop = function () { io.disconnect(); leave(); };
  }

  /* ---------------------------------------------------------------------
     Focus trap + Escape helpers for the rail modal and the explorer drawer
     --------------------------------------------------------------------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), video[controls], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  function trapTab(container, e) {
    var items = qsa(container, FOCUSABLE).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* =====================================================================
     1. Interrupt rail (InterruptRail.tsx)
     ===================================================================== */
  function initRail(root) {
    if (!root || root.__brawInit) return;
    root.__brawInit = true;

    qsa(root, 'video[data-braw-loop]').forEach(initLoopVideo);

    var scroller = qs(root, '[data-braw-rail-scroller]');
    qsa(root, '[data-braw-rail-scroll]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!scroller) return;
        var dir = Number(btn.getAttribute('data-braw-rail-scroll')) || 1;
        var firstTile = qs(scroller, '[data-rail-tile]');
        var step = firstTile ? firstTile.offsetWidth + 12 : scroller.clientWidth * 0.8;
        if (typeof scroller.scrollBy === 'function') scroller.scrollBy({ left: dir * step, behavior: 'smooth' });
        else scroller.scrollLeft += dir * step;
      });
    });

    var modal = qs(root, '[data-braw-rail-modal]');
    var modalVideoWrap = qs(root, '[data-braw-rail-modal-video]');
    var closeBtn = qs(root, '[data-braw-rail-close]');
    var lastFocus = null;

    function closeModal() {
      if (!modal || modal.hidden) return;
      var v = qs(modalVideoWrap, 'video');
      if (v) { try { v.pause(); } catch (e) { /* ignore */ } v.removeAttribute('src'); v.load(); }
      if (modalVideoWrap) modalVideoWrap.innerHTML = '';
      modal.hidden = true;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }
    function openModal(src, label) {
      if (!modal || !modalVideoWrap) return;
      lastFocus = document.activeElement;
      modalVideoWrap.innerHTML = '';
      var v = document.createElement('video');
      v.src = src;
      v.controls = true;
      v.setAttribute('playsinline', '');
      v.playsInline = true;
      if (label) v.setAttribute('aria-label', label);
      if (REDUCED_MOTION) {
        /* lib/use-reduced-motion.ts: never autoplay under reduced motion */
        v.currentTime = 0;
      } else {
        v.autoplay = true;
        v.setAttribute('autoplay', '');
      }
      modalVideoWrap.appendChild(v);
      modal.hidden = false;
      if (!REDUCED_MOTION) {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(noop);
      }
      if (closeBtn) closeBtn.focus();
    }

    qsa(root, '[data-braw-rail-full]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        openModal(tile.getAttribute('data-braw-rail-full'), tile.getAttribute('aria-label'));
      });
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (!modal || modal.hidden) return;
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'Tab') trapTab(modal, e);
    });
  }

  /* =====================================================================
     2. The Trip (TheTrip.tsx + media.tsx StillThenVideo)
     ===================================================================== */
  var tripRoots = [];

  function initSequence(wrap) {
    var video = qs(wrap, 'video');
    if (!video || video.__brawSeq) return;
    video.__brawSeq = true;
    if (REDUCED_MOTION) {
      if (video.parentNode) video.parentNode.removeChild(video);
      wrap.classList.remove('is-playing');
      return;
    }
    var order = wrap.getAttribute('data-order') || 'still-first';
    var hold = Number(wrap.getAttribute('data-hold')) || 1400;
    var holdAfter = Number(wrap.getAttribute('data-hold-after')) || 2500;
    var videoFirst = order === 'video-first';
    var timer = null;

    video.muted = true;
    video.defaultMuted = true;

    var fb = createFallback(video, function () {
      clearHold();
      wrap.classList.remove('is-playing');
    });

    function clearHold() {
      if (timer !== null) { clearTimeout(timer); timer = null; }
    }
    function stop() {
      wrap.classList.remove('is-playing');
      if (fb.failed) return;
      try { video.pause(); video.currentTime = 0; } catch (e) { /* ignore */ }
    }
    function play() {
      if (fb.failed) return;
      wrap.classList.add('is-playing');
      video.muted = true;
      try { video.currentTime = 0; } catch (e) { /* ignore */ }
      fb.arm(true);
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(noop);
    }
    video.addEventListener('ended', function () {
      stop();
      if (!fb.failed) timer = setTimeout(play, videoFirst ? holdAfter : hold);
    });

    function enter() {
      fb.inView = true;
      video.preload = 'auto';
      clearHold();
      if (videoFirst) {
        play();
      } else {
        stop();
        if (!fb.failed) timer = setTimeout(play, hold);
      }
    }
    function leave() {
      fb.inView = false;
      fb.disarm();
      clearHold();
      stop();
    }
    if (!HAS_IO) { enter(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) enter(); else leave();
      });
    }, { threshold: 0.5 });
    io.observe(wrap);
  }

  function initTrip(root) {
    if (!root || root.__brawInit) return;
    root.__brawInit = true;
    tripRoots.push(root);

    var track = qs(root, '[data-braw-trip-track]');
    var chapters = qsa(root, '[data-braw-trip-chapter]');
    var markers = qsa(root, '[data-braw-trip-marker]');
    var nodes = qsa(root, '[data-braw-trip-node]');
    var fill = qs(root, '[data-braw-trip-fill]');
    var pill = qs(root, '[data-braw-trip-pill]');
    var tops = [];
    var active = 0;

    function recompute() {
      if (!track) return;
      var top = track.getBoundingClientRect().top;
      tops = markers.map(function (el) {
        var r = el.getBoundingClientRect();
        return r.top - top + r.height / 2;
      });
      nodes.forEach(function (n, i) { n.style.top = ((tops[i] || 0) - 5) + 'px'; });
      if (fill) fill.style.height = (tops[active] || 0) + 'px';
    }
    function setActive(i) {
      if (i === active) return;
      active = i;
      chapters.forEach(function (c, j) { c.classList.toggle('is-active', j === i); });
      if (pill) {
        pill.textContent = chapters[i].getAttribute('data-marker') || '';
        pill.classList.remove('is-in');
        void pill.offsetWidth;
        pill.classList.add('is-in');
      }
      if (fill) fill.style.height = (tops[active] || 0) + 'px';
    }

    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('load', recompute);
    if ('ResizeObserver' in window && track) {
      new ResizeObserver(recompute).observe(track);
    }

    if (HAS_IO) {
      var activeIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var idx = Number(entry.target.getAttribute('data-chapter-index'));
          if (!isNaN(idx)) setActive(idx);
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      chapters.forEach(function (c) { activeIo.observe(c); });

      var revealIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealIo.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
      qsa(root, '[data-braw-reveal]').forEach(function (el) { revealIo.observe(el); });
    } else {
      qsa(root, '[data-braw-reveal]').forEach(function (el) { el.classList.add('is-revealed'); });
    }

    qsa(root, 'video[data-braw-loop]').forEach(initLoopVideo);
    qsa(root, '[data-braw-seq]').forEach(initSequence);

    updateTrip(root, readState());
  }

  /* Add-on link label: "Included in the Full BRAW Kit" for a kit bundle,
     "WEE Pump added" once it is in the single build, else the source label.
     Disabled unless the bundle is single (source: disabled={bundle !== "single"}). */
  function updateTrip(root, state) {
    qsa(root, '[data-braw-trip-addon]').forEach(function (btn) {
      var addon = btn.getAttribute('data-addon') || 'wee-pump';
      var isKit = state.bundle === 'full-kit' || state.bundle === 'two-kits';
      var added = addon === 'wee-pump' && state.bundle === 'single' && state.has('wee-pump');
      var label = btn.getAttribute('data-label') || btn.textContent;
      btn.textContent = isKit ? 'Included in the Full BRAW Kit' : (added ? 'WEE Pump added' : label);
      btn.disabled = state.bundle !== 'single';
    });
  }

  /* =====================================================================
     3. Product explorer (ProductExplorer.tsx + media.tsx
        VideoOnceRevealMedia / VideoLoop)
     ===================================================================== */
  var explorerRoots = [];

  function initExplorer(root) {
    if (!root || root.__brawInit) return;
    root.__brawInit = true;
    explorerRoots.push(root);

    var tabs = qsa(root, '[data-braw-explorer-tab]');
    var views = qsa(root, '[data-braw-explorer-view]');
    var lists = qsa(root, '[data-braw-explorer-list]');
    var viewIds = views.map(function (v) { return v.getAttribute('data-braw-explorer-view'); });
    var current = 0;

    /* ---- CLIPPOD: play once, then hold the still and reveal the pins ---- */
    var videoView = qs(root, '[data-braw-explorer-video-view]');
    var onceVideo = videoView ? qs(videoView, '[data-braw-explorer-once]') : null;
    var once = { done: false, failed: false, safety: null, fb: null, inView: false };

    function videoViewActive() {
      return !!videoView && viewIds[current] === videoView.getAttribute('data-braw-explorer-view');
    }
    function setPinsVisible(view, visible) {
      qsa(view, '[data-braw-hotspot]').forEach(function (pin) {
        if (!pin.classList.contains('braw-explorer__pin')) return;
        pin.classList.toggle('is-hidden', !visible);
        pin.disabled = !visible;
      });
    }
    function setDone(d) {
      if (!videoView) return;
      once.done = d;
      videoView.classList.toggle('is-done', d);
      var visible = d || once.failed || REDUCED_MOTION;
      setPinsVisible(videoView, visible);
      if (d && once.safety) { clearTimeout(once.safety); once.safety = null; }
    }
    function armSafety() {
      if (once.safety) clearTimeout(once.safety);
      /* media.tsx: reveal the pins 6s after the tab becomes active whatever happens */
      once.safety = setTimeout(function () { setDone(true); }, 6000);
    }
    function replay() {
      if (!videoView) return;
      if (REDUCED_MOTION || once.failed || !onceVideo || !onceVideo.parentNode) { setDone(true); return; }
      setDone(false);
      armSafety();
      onceVideo.muted = true;
      onceVideo.preload = 'auto';
      try { onceVideo.currentTime = 0; } catch (e) { /* ignore */ }
      if (once.fb) once.fb.arm(true);
      var p = onceVideo.play();
      if (p && typeof p.catch === 'function') p.catch(noop);
    }

    if (videoView && onceVideo) {
      if (REDUCED_MOTION) {
        if (onceVideo.parentNode) onceVideo.parentNode.removeChild(onceVideo);
        once.failed = true;
        videoView.classList.add('is-failed');
        setDone(true);
      } else {
        onceVideo.muted = true;
        onceVideo.defaultMuted = true;
        once.fb = createFallback(onceVideo, function () {
          once.failed = true;
          videoView.classList.add('is-failed');
          setDone(true);
        });
        onceVideo.addEventListener('ended', function () { setDone(true); });
        if (HAS_IO) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              once.inView = entry.isIntersecting;
              if (once.fb) once.fb.inView = entry.isIntersecting;
              if (entry.isIntersecting && videoViewActive() && !once.done && onceVideo.paused && !once.failed) {
                replay();
              }
            });
          }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }).observe(videoView);
        }
      }
      var replayBtn = qs(videoView, '[data-braw-explorer-replay]');
      if (replayBtn) replayBtn.addEventListener('click', replay);
    }

    /* ---- Tabs ---- */
    function selectView(i, fromGesture) {
      current = i;
      tabs.forEach(function (t, j) {
        var on = j === i;
        t.classList.toggle('is-selected', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      views.forEach(function (v, j) { v.hidden = j !== i; });
      lists.forEach(function (l, j) { l.hidden = j !== i; });
      closeDrawer();
      if (videoView) {
        if (videoViewActive()) {
          if (REDUCED_MOTION || once.failed) { setDone(true); }
          else {
            /* Direct play() from the click gesture satisfies autoplay
               policy on phones (source: replayRef.current?.replay()). */
            setDone(false);
            if (fromGesture) replay(); else armSafety();
          }
        } else {
          setDone(false);
          if (once.safety) { clearTimeout(once.safety); once.safety = null; }
          if (onceVideo && onceVideo.parentNode) { try { onceVideo.pause(); } catch (e) { /* ignore */ } }
        }
      }
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectView(i, true); });
      tab.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = tabs.length - 1;
        if (n === null) return;
        e.preventDefault();
        tabs[n].focus();
        selectView(n, true);
      });
    });

    /* ---- Drawer ---- */
    var overlay = qs(root, '[data-braw-explorer-overlay]');
    var drawer = qs(root, '[data-braw-explorer-drawer]');
    var drawerBody = qs(root, '[data-braw-explorer-body]');
    var drawerNumber = qs(root, '[data-braw-explorer-number]');
    var drawerCounter = qs(root, '[data-braw-explorer-counter]');
    var closeBtn = qs(root, '[data-braw-explorer-close]');
    var activeId = null;
    var lastFocus = null;
    var dragY = 0;
    var touchStartY = null;

    function currentIds() {
      var list = lists[current];
      return qsa(list, '[data-braw-hotspot]').map(function (b) { return b.getAttribute('data-braw-hotspot'); });
    }
    function setDrag(y) {
      dragY = y;
      if (!drawer) return;
      drawer.style.transform = y ? 'translateY(' + y + 'px)' : '';
    }
    function markActivePins() {
      qsa(root, '.braw-explorer__pin').forEach(function (pin) {
        pin.classList.toggle('is-active', pin.getAttribute('data-braw-hotspot') === activeId);
      });
    }
    function stopDrawerVideo() {
      if (!drawerBody) return;
      qsa(drawerBody, 'video').forEach(function (v) {
        if (v.__brawLoopStop) v.__brawLoopStop();
        try { v.pause(); } catch (e) { /* ignore */ }
        v.removeAttribute('src');
        try { v.load(); } catch (e) { /* ignore */ }
      });
      drawerBody.innerHTML = '';
    }
    function openHotspot(id) {
      var tpl = qs(root, '[data-braw-hotspot-panel="' + id + '"]');
      if (!tpl || !drawer || !drawerBody) return;
      var wasOpen = !drawer.hidden;
      if (!wasOpen) lastFocus = document.activeElement;
      activeId = id;
      setDrag(0);
      stopDrawerVideo();
      var frag = tpl.content ? document.importNode(tpl.content, true) : null;
      if (frag) drawerBody.appendChild(frag);
      else drawerBody.innerHTML = tpl.innerHTML;
      drawer.setAttribute('aria-label', tpl.getAttribute('data-name') || '');
      if (drawerNumber) drawerNumber.textContent = tpl.getAttribute('data-number') || '';
      var ids = currentIds();
      var idx = ids.indexOf(id);
      if (drawerCounter) drawerCounter.textContent = (idx + 1) + ' / ' + ids.length;
      /* HotspotClip: mounted fresh each time, autoplays in view, still only
         under reduced motion. */
      qsa(drawerBody, 'video[data-braw-loop]').forEach(initLoopVideo);
      updateExplorer(root, readState());
      if (overlay) overlay.hidden = false;
      drawer.hidden = false;
      drawer.scrollTop = 0;
      markActivePins();
      if (!wasOpen && closeBtn) closeBtn.focus();
    }
    function closeDrawer() {
      if (!drawer || drawer.hidden) { activeId = null; markActivePins(); return; }
      activeId = null;
      setDrag(0);
      stopDrawerVideo();
      drawer.hidden = true;
      if (overlay) overlay.hidden = true;
      markActivePins();
      if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) lastFocus.focus();
      lastFocus = null;
    }
    function step(delta) {
      if (activeId === null) return;
      var ids = currentIds();
      var idx = ids.indexOf(activeId);
      if (idx < 0) return;
      openHotspot(ids[(idx + delta + ids.length) % ids.length]);
    }

    qsa(root, '[data-braw-hotspot]').forEach(function (btn) {
      btn.addEventListener('click', function () { openHotspot(btn.getAttribute('data-braw-hotspot')); });
    });
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    qsa(root, '[data-braw-explorer-step]').forEach(function (btn) {
      btn.addEventListener('click', function () { step(Number(btn.getAttribute('data-braw-explorer-step')) || 1); });
    });
    if (drawer) {
      drawer.addEventListener('touchstart', function (e) {
        if (drawer.scrollTop > 0) { touchStartY = null; return; }
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      drawer.addEventListener('touchmove', function (e) {
        if (touchStartY === null) return;
        var d = e.touches[0].clientY - touchStartY;
        if (d > 0) { drawer.classList.add('is-dragging'); setDrag(d); }
      }, { passive: true });
      drawer.addEventListener('touchend', function () {
        drawer.classList.remove('is-dragging');
        if (dragY > 80) closeDrawer(); else setDrag(0);
        touchStartY = null;
      });
      /* The drawer's "Added · Remove" button is a toggle-addon action handled
         by braw-pdp.js; the "Add the WEE Pump · £19.99" button is
         add-addon-and-scroll, which scrolls to the buy box, so close the
         drawer first so it does not sit over the hero. */
      drawer.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target.closest('[data-braw-action="add-addon-and-scroll"]') : null;
        if (t) closeDrawer();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (!drawer || drawer.hidden) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key === 'Tab') trapTab(drawer, e);
    });

    /* Initial state */
    selectView(0, false);
    updateExplorer(root, readState());
  }

  /* Add-on hotspot presentation (source: weePumpAdded / isKit branches). */
  function updateExplorer(root, state) {
    var isKit = state.bundle === 'full-kit' || state.bundle === 'two-kits';
    var weePumpAdded = state.bundle === 'single' && state.has('wee-pump');

    qsa(root, '[data-braw-hotspot-addon]').forEach(function (pin) {
      pin.classList.toggle('is-added', weePumpAdded);
    });
    qsa(root, '[data-braw-explorer-addon-name]').forEach(function (el) {
      var name = el.getAttribute('data-name') || el.textContent;
      el.textContent = isKit ? 'Included in the Full BRAW Kit' : (weePumpAdded ? 'WEE Pump added' : name);
      el.classList.toggle('is-muted', weePumpAdded || isKit);
    });

    var price = null;
    var cat = readCatalogue();
    if (cat && cat.weePump && cat.weePump.variants && cat.weePump.variants[0] && typeof cat.weePump.variants[0].price === 'number') {
      price = formatPrice(cat.weePump.variants[0].price);
    }
    qsa(root, '[data-braw-explorer-addon]').forEach(function (block) {
      var kitP = qs(block, '[data-braw-explorer-addon-kit]');
      var addBtn = qs(block, '[data-braw-explorer-addon-add]');
      var removeBtn = qs(block, '[data-braw-explorer-addon-remove]');
      var addon = (addBtn && addBtn.getAttribute('data-addon')) || 'wee-pump';
      var added = state.has(addon);
      if (kitP) kitP.hidden = !isKit;
      if (addBtn) {
        addBtn.hidden = isKit || added;
        var name = addBtn.getAttribute('data-name') || '';
        var p = price || addBtn.getAttribute('data-price') || '';
        addBtn.textContent = name + ' · ' + p;
      }
      if (removeBtn) removeBtn.hidden = isKit || !added;
    });
  }

  /* =====================================================================
     Boot
     ===================================================================== */
  function initAll(scope) {
    scope = scope || document;
    qsa(scope, '[data-braw-rail]').forEach(initRail);
    qsa(scope, '[data-braw-trip]').forEach(initTrip);
    qsa(scope, '[data-braw-explorer]').forEach(initExplorer);
    if (scope !== document) {
      if (scope.matches && scope.matches('[data-braw-rail]')) initRail(scope);
      if (scope.matches && scope.matches('[data-braw-trip]')) initTrip(scope);
      if (scope.matches && scope.matches('[data-braw-explorer]')) initExplorer(scope);
    }
  }

  function onChange(e) {
    var state = readState(e && e.detail);
    tripRoots.forEach(function (r) { updateTrip(r, state); });
    explorerRoots.forEach(function (r) { updateExplorer(r, state); });
  }

  function boot() {
    initAll(document);
    document.addEventListener('braw:change', onChange);
    /* Theme editor re-renders */
    document.addEventListener('shopify:section:load', function (e) {
      if (e && e.target) initAll(e.target);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();


/* === C === */
/* =====================================================================
   AGENT C: Explorer 2 reviews lightbox + expand, Complete Your Kit and
   Final CTA re-rendering from the shared BRAW state (braw:change), and the
   two accessory pages' own tiny state engines (WEE Pump, Wash Bag):
   add to cart -> upsell sheet -> fetch('/cart/add.js') -> /checkout,
   sticky bar visibility, gallery / colour picker.
   Ported from: Reviews.tsx, CompleteYourKit.tsx, FinalCTA.tsx,
   wee-pump-context.tsx, WeePumpHero.tsx, WeePumpStickyBar.tsx,
   WeePumpUpsellSheet.tsx, UpsellSheetShell.tsx, wash-bag-context.tsx,
   WashBagHero.tsx, WashBagFeature.tsx, WashBagStickyBar.tsx,
   WashBagUpsellSheet.tsx, use-sticky-cart-visibility.ts.
   Every block is guarded by a root-element check so it no-ops elsewhere.
   ===================================================================== */
(function () {
  "use strict";

  var doc = document;
  var CHECK_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" class="braw-icon braw-icon--check"><path d="M20 6 9 17l-5-5"/></svg>';
  var CART_ERROR = "Something went wrong adding to your cart. Please try again.";
  var EXPLORER_LABELS = {
    "black-orange": "Black + Orange",
    "blue-orange": "Blue + Orange",
    "blue-stripes": "Blue + Stripes",
  };
  var WASH_BAG_LABELS = {
    "black-orange": "Black + Orange",
    "blue-stripes": "Blue + Stripes",
  };

  function formatPrice(pence) {
    if (window.BRAW && typeof window.BRAW.formatPrice === "function") {
      try { return window.BRAW.formatPrice(pence); } catch (e) { /* fall through */ }
    }
    var n = Math.round(Number(pence) || 0);
    var meta = document.querySelector('meta[property="og:price:currency"]');
    var currency = (meta && meta.getAttribute('content')) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: n % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
      }).format(n / 100).replace(/\u00a0/g, ' ');
    } catch (e) {
      return currency + ' ' + (n / 100).toFixed(n % 100 === 0 ? 0 : 2);
    }
  }

  function defaultWashBagColorId(colorId) {
    return colorId === "black-orange" ? "black-orange" : "blue-stripes";
  }

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "null");
    } catch (e) {
      return null;
    }
  }

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }
  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }
  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute("hidden", "");
    else el.removeAttribute("hidden");
  }
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // Shared add-to-cart -> checkout. Never clears the cart first.
  // Returns a promise resolving to true when navigation has been started.
  function addLinesAndCheckout(lines) {
    var items = lines
      .filter(function (l) {
        return l && l.id > 0 && l.quantity > 0;
      })
      .map(function (l) {
        return { id: l.id, quantity: l.quantity };
      });
    if (items.length === 0) return Promise.resolve(false);
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      if (!res.ok) throw new Error("cart_add_failed");
      window.location.assign("/checkout");
      return true;
    });
  }

  // Focus trap + Escape for a dialog panel. Returns a teardown function.
  function trapDialog(panel, onClose) {
    var previouslyFocused = doc.activeElement;
    var bodyOverflow = doc.body.style.overflow;
    doc.body.style.overflow = "hidden";
    function focusables() {
      return qsa(
        panel,
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ).filter(function (el) {
        return !el.closest("[hidden]");
      });
    }
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      var list = focusables();
      if (list.length === 0) return;
      var first = list[0];
      var last = list[list.length - 1];
      var active = doc.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
    doc.addEventListener("keydown", onKeyDown);
    return function teardown() {
      doc.removeEventListener("keydown", onKeyDown);
      doc.body.style.overflow = bodyOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }

  // Port of use-sticky-cart-visibility.ts: show once the visitor has
  // scrolled past the bottom of the hero buy button, hide while it is back
  // in view. Also pads the body by the bar height while visible.
  function stickyBarController(bar, buttonId, extraHide) {
    if (!bar) return { update: function () {} };
    var buttonBottom = Infinity;
    var scheduled = false;
    var visible = false;
    function measure() {
      var el = doc.getElementById(buttonId);
      buttonBottom = el ? el.getBoundingClientRect().bottom + window.scrollY : Infinity;
    }
    function applyPadding() {
      if (visible) {
        doc.body.style.paddingBottom = bar.getBoundingClientRect().height + "px";
      } else {
        doc.body.style.paddingBottom = "";
      }
    }
    function check() {
      scheduled = false;
      var next = window.scrollY > buttonBottom && !(extraHide && extraHide());
      if (next !== visible) {
        visible = next;
        bar.classList.toggle("is-visible", visible);
        bar.setAttribute("aria-hidden", visible ? "false" : "true");
      }
      applyPadding();
    }
    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(check);
    }
    function onResize() {
      measure();
      onScroll();
    }
    measure();
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("load", onResize);
    return { update: onResize };
  }

  // Simple gallery: [data-braw-gallery-slide=i] images + [data-braw-gallery-thumb=i]
  // buttons inside a root. Ported from the Gallery() in WeePumpHero.tsx /
  // WashBagHero.tsx.
  function initGallery(root) {
    if (!root) return;
    function select(i) {
      qsa(root, "[data-braw-gallery-slide]").forEach(function (img) {
        var on = img.getAttribute("data-braw-gallery-slide") === String(i);
        setHidden(img, !on);
        if (on && img.getAttribute("loading") === "lazy") img.removeAttribute("loading");
      });
      qsa(root, "[data-braw-gallery-thumb]").forEach(function (btn) {
        btn.setAttribute(
          "aria-pressed",
          btn.getAttribute("data-braw-gallery-thumb") === String(i) ? "true" : "false",
        );
      });
    }
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-braw-gallery-thumb]");
      if (!btn || !root.contains(btn)) return;
      select(Number(btn.getAttribute("data-braw-gallery-thumb")));
    });
    return { select: select };
  }

  /* =================================================================
     Explorer 2: Reviews (expand + photo lightbox). Reviews.tsx
     ================================================================= */
  (function initReviews() {
    var root = doc.getElementById("braw-reviews");
    if (!root) return;
    var cards = qs(root, "[data-braw-review-cards]");
    var more = qs(root, "[data-braw-reviews-more]");
    var expandBtn = qs(root, "[data-braw-reviews-expand]");
    if (expandBtn) {
      expandBtn.addEventListener("click", function () {
        qsa(root, "[data-braw-review-card]").forEach(function (c) {
          setHidden(c, false);
        });
        if (cards) cards.classList.add("is-expanded");
        setHidden(more, true);
      });
    }

    var lightbox = qs(root, "[data-braw-lightbox]");
    var dialog = qs(root, "[data-braw-lightbox-dialog]");
    var closeBtn = qs(root, "[data-braw-lightbox-close]");
    var teardown = null;

    function closeLightbox() {
      if (!lightbox || lightbox.hasAttribute("hidden")) return;
      setHidden(lightbox, true);
      if (teardown) teardown();
      teardown = null;
    }
    function openLightbox(index) {
      var card = qs(root, '[data-braw-review-card="' + index + '"]');
      var photo = card ? qs(card, "[data-braw-review-photo]") : null;
      if (!card || !photo || !lightbox) return;
      var name = qs(card, "[data-braw-review-name]");
      var date = qs(card, "[data-braw-review-date]");
      var quote = qs(card, "[data-braw-review-quote]");
      var item = qs(card, "[data-braw-review-item]");
      var img = qs(lightbox, "[data-braw-lightbox-img]");
      img.src = photo.getAttribute("src");
      img.alt = photo.getAttribute("alt") || "";
      img.width = Number(photo.getAttribute("width")) || 500;
      img.height = Number(photo.getAttribute("height")) || 500;
      setText(qs(lightbox, "[data-braw-lightbox-date]"), date ? date.textContent : "");
      setText(qs(lightbox, "[data-braw-lightbox-quote]"), quote ? quote.textContent : "");
      setText(qs(lightbox, "[data-braw-lightbox-name]"), name ? name.textContent : "");
      var itemEl = qs(lightbox, "[data-braw-lightbox-item]");
      var dotEl = qs(lightbox, "[data-braw-lightbox-dot]");
      setText(itemEl, item ? item.textContent : "");
      setHidden(itemEl, !item);
      setHidden(dotEl, !item);
      if (dialog) {
        dialog.setAttribute(
          "aria-label",
          "Photo and review from " + (name ? name.textContent : ""),
        );
      }
      setHidden(lightbox, false);
      teardown = trapDialog(dialog || lightbox, closeLightbox);
      if (closeBtn) closeBtn.focus();
    }

    root.addEventListener("click", function (e) {
      var thumb = e.target.closest("[data-braw-review-thumb]");
      if (thumb && root.contains(thumb)) {
        openLightbox(thumb.getAttribute("data-braw-review-thumb"));
        return;
      }
      if (e.target.closest("[data-braw-lightbox-close]")) {
        closeLightbox();
        return;
      }
      if (e.target.closest("[data-braw-lightbox-backdrop]")) {
        closeLightbox();
      }
    });
  })();

  /* =================================================================
     Explorer 2: Complete Your Kit + Final CTA re-render on braw:change.
     The shared engine (braw-pdp.js) owns the state, click delegation,
     [data-braw-price], [data-braw-switch-notice], [data-braw-addon-nudge]
     and [data-braw-washbag-picker]; this block layers the parts of
     CompleteYourKit.tsx / FinalCTA.tsx that go beyond those hooks.
     ================================================================= */
  (function initExplorerKit() {
    var kitRoot = doc.getElementById("braw-complete-your-kit");
    var finalRoot = doc.getElementById("braw-final-cta");
    if (!kitRoot && !finalRoot) return;

    var combos = readJson(kitRoot ? qs(kitRoot, "[data-braw-kit-combos]") : null) || [];

    function hasAddOn(state, id) {
      var s = state.addOnIds;
      if (!s) return false;
      if (typeof s.has === "function") return s.has(id);
      if (Array.isArray(s)) return s.indexOf(id) !== -1;
      return false;
    }

    function kitVariantExists(colorId, washBagColorId) {
      if (!combos.length) return true;
      var e = EXPLORER_LABELS[colorId] || EXPLORER_LABELS["black-orange"];
      var w = WASH_BAG_LABELS[washBagColorId] || WASH_BAG_LABELS["black-orange"];
      return combos.some(function (c) {
        return c.e === e && c.w === w;
      });
    }

    function renderBuyLabel(root, state, fullLabel) {
      var label = qs(root, "[data-braw-c-buy-label]");
      var price = qs(root, "[data-braw-c-buy-price]");
      if (!label) return;
      if (state.added) {
        label.innerHTML = CHECK_ICON + " " + fullLabel;
        setHidden(price, true);
      } else if (state.pendingCheckout) {
        setText(label, "Go to checkout");
        setHidden(price, true);
      } else {
        setText(label, "Add to cart, ");
        setHidden(price, false);
      }
    }

    function render(state) {
      if (!state) return;
      var bundle = state.bundle || "single";
      var isKit = bundle === "full-kit" || bundle === "two-kits";
      var washBagColorId = state.washBagColorId || "black-orange";

      if (kitRoot) {
        var wbSwatch = qs(kitRoot, '[data-braw-washbag-swatch][data-color="' + washBagColorId + '"]');
        var wbPrice = wbSwatch ? Number(wbSwatch.getAttribute("data-price")) : NaN;
        var wbAvailable = wbSwatch ? wbSwatch.getAttribute("data-available") !== "false" : true;

        qsa(kitRoot, "[data-braw-kit-item]").forEach(function (item) {
          var id = item.getAttribute("data-braw-kit-item");
          var toggle = qs(item, "[data-braw-addon]");
          var desc = qs(item, "[data-braw-kit-desc]");
          var priceEl = qs(item, "[data-braw-kit-price]");
          // Wash bag availability follows the selected colour; the pump's
          // comes from the server render (it has a single variant).
          var available =
            id === "wash-bag"
              ? wbAvailable
              : !(toggle && toggle.hasAttribute("data-initial-disabled"));
          var soldOut = !available;
          var on = isKit || hasAddOn(state, id);
          item.classList.toggle("is-on", on);
          item.classList.toggle("is-sold-out", soldOut);
          if (toggle) {
            toggle.setAttribute("aria-pressed", on ? "true" : "false");
            toggle.disabled = isKit || soldOut;
          }
          if (desc) {
            setText(
              desc,
              isKit
                ? "Included in the Full BRAW Kit."
                : soldOut
                  ? "Currently sold out."
                  : desc.getAttribute("data-description") || "",
            );
          }
          if (priceEl) {
            setHidden(priceEl, isKit);
            if (!isKit) {
              if (soldOut) setText(priceEl, "Sold out");
              else if (id === "wee-pump") setText(priceEl, formatPrice(window.BRAW && window.BRAW.weePump ? window.BRAW.weePump.price : 1999));
              else if (id === "wash-bag" && !isNaN(wbPrice)) setText(priceEl, formatPrice(wbPrice));
            }
          }
        });

        setText(qs(kitRoot, "[data-braw-c-washbag-label]"), WASH_BAG_LABELS[washBagColorId] || "");
        qsa(kitRoot, "[data-braw-washbag-swatch]").forEach(function (sw) {
          sw.setAttribute("aria-pressed", sw.getAttribute("data-color") === washBagColorId ? "true" : "false");
        });
        var showPicker = bundle === "full-kit" || hasAddOn(state, "wash-bag");
        setHidden(qs(kitRoot, "[data-braw-washbag-picker]"), !showPicker);

        var estimate = qs(kitRoot, "[data-braw-c-kit-estimate]");
        setHidden(estimate, !(isKit && !kitVariantExists(state.colorId || "black-orange", washBagColorId)));

        var notice = qs(kitRoot, "[data-braw-switch-notice]");
        if (notice && typeof state.switchNotice !== "undefined") {
          setText(notice, state.switchNotice || "");
          setHidden(notice, !state.switchNotice);
        }
        var nudge = qs(kitRoot, "[data-braw-addon-nudge]");
        if (nudge && typeof state.addOnNudge !== "undefined") {
          setText(nudge, state.addOnNudge || "");
          setHidden(nudge, !state.addOnNudge);
        }
        renderBuyLabel(kitRoot, state, "Added to cart");
      }

      if (finalRoot) {
        renderBuyLabel(finalRoot, state, "Added to cart");
      }
    }

    // Remember server-rendered sold-out state for the pump so re-renders keep it.
    if (kitRoot) {
      var pumpToggle = qs(kitRoot, '[data-braw-kit-item="wee-pump"] [data-braw-addon]');
      if (pumpToggle && pumpToggle.disabled) pumpToggle.setAttribute("data-initial-disabled", "true");
    }

    doc.addEventListener("braw:change", function (e) {
      render(e.detail);
    });
    // If the shared engine already initialised (and dispatched its first
    // braw:change) before this listener existed, e.g. with ?bundle=kit in
    // the URL, pull its current state once, or ask it to re-render so the
    // event fires again. Runs after DOMContentLoaded so the engine's own
    // init has had its turn first.
    function syncFromEngine() {
      var B = window.BRAW;
      if (!B) return;
      var s = B.state || (typeof B.getState === "function" ? B.getState() : null);
      if (s) {
        render(s);
      } else if (typeof B.render === "function") {
        try {
          B.render();
        } catch (e) {
          /* engine not ready; the next state change re-renders us */
        }
      }
    }
    if (doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", syncFromEngine);
    } else {
      syncFromEngine();
    }
  })();

  /* =================================================================
     Accessory page engine, shared by the WEE Pump and Wash Bag pages.
     Ported from wee-pump-context.tsx / wash-bag-context.tsx.
     ================================================================= */
  function accessoryPage(cfg) {
    var hero = doc.getElementById(cfg.heroId);
    if (!hero) return;
    var prefix = cfg.prefix;
    var data = readJson(qs(hero, "[data-braw-" + prefix + "-data]"));
    if (!data) return;
    var sheetRoot = doc.getElementById(cfg.sheetId);
    var sheet = sheetRoot ? qs(sheetRoot, "[data-braw-" + prefix + "-sheet]") : null;
    var panel = sheet ? qs(sheet, "[data-braw-" + prefix + "-sheet-panel]") : null;
    var stickyRoot = doc.getElementById(cfg.stickyId);
    var bar = stickyRoot ? qs(stickyRoot, "[data-braw-" + prefix + "-sticky]") : null;

    var state = {
      added: false,
      adding: false,
      upsellOpen: false,
      pendingCheckout: false,
      explorerColorId: "black-orange",
      colorId: cfg.initialColorId || null,
    };
    var addedTimer = null;
    var checkoutInFlight = false;
    var upsellShownInFlight = false;
    var sheetTeardown = null;

    var explorerColors = Array.isArray(data.explorer) ? data.explorer : [];
    var kitVariants = Array.isArray(data.kit) ? data.kit : [];

    function explorerColor() {
      var id = cfg.fixedExplorerColorId || state.explorerColorId;
      return (
        explorerColors.filter(function (c) {
          return c.id === id;
        })[0] || explorerColors[0] || null
      );
    }
    function findKitVariant(explorerLabel, washBagLabel) {
      return (
        kitVariants.filter(function (v) {
          return v.e === explorerLabel && v.w === washBagLabel;
        })[0] || null
      );
    }
    function kitVariant() {
      var ec = explorerColor();
      if (!ec) return null;
      var washLabel = cfg.washBagLabelForKit ? cfg.washBagLabelForKit(state) : WASH_BAG_LABELS[defaultWashBagColorId(ec.id)];
      return findKitVariant(ec.label, washLabel);
    }
    function kitPrice() {
      var kv = kitVariant();
      return kv ? kv.price : 13500;
    }

    // Current sellable line for this page (pump variant or selected wash bag colour).
    function current() {
      return cfg.current(state, data);
    }

    function showError(msg) {
      qsa(doc, "[data-braw-" + prefix + "-error]").forEach(function (el) {
        setText(el, msg || "");
        setHidden(el, !msg);
      });
    }

    function render() {
      var cur = current();
      var priceText = formatPrice(cur.price);
      var hasCompare = cur.compareAt != null && cur.compareAt > cur.price;
      qsa(doc, "[data-braw-" + prefix + "-price]").forEach(function (el) {
        setText(el, priceText);
      });
      qsa(doc, "[data-braw-" + prefix + "-compare]").forEach(function (el) {
        setText(el, hasCompare ? formatPrice(cur.compareAt) : "");
        setHidden(el, !hasCompare);
      });
      qsa(doc, "[data-braw-" + prefix + "-buy]").forEach(function (btn) {
        var kind = btn.getAttribute("data-braw-" + prefix + "-buy");
        var label = qs(btn, "[data-braw-" + prefix + "-buy-label]") || btn;
        btn.disabled = !cur.available || state.adding;
        if (state.adding) {
          setText(label, "Adding...");
        } else if (state.added) {
          label.innerHTML = CHECK_ICON + " " + (kind === "sticky" ? "Added" : "Added to cart");
        } else if (state.pendingCheckout) {
          setText(label, "Go to checkout");
        } else if (kind === "sticky") {
          setText(label, "Add to cart");
        } else if (cur.available) {
          setText(label, "Add to cart, " + priceText);
        } else {
          setText(label, "Sold out");
        }
      });
      qsa(doc, "[data-braw-" + prefix + "-kit-price]").forEach(function (el) {
        setText(el, formatPrice(kitPrice()));
      });
      if (sheet) {
        var title = cfg.sheetTitle(cur);
        setText(qs(sheet, "[data-braw-" + prefix + "-sheet-title]"), title);
        if (panel) panel.setAttribute("aria-label", title);
        qsa(sheet, "[data-braw-" + prefix + "-explorer-color]").forEach(function (chip) {
          chip.setAttribute(
            "aria-pressed",
            chip.getAttribute("data-braw-" + prefix + "-explorer-color") === state.explorerColorId ? "true" : "false",
          );
        });
      }
      if (cfg.render) cfg.render(state, data, cur);
    }

    function setAdded() {
      state.added = true;
      if (addedTimer) clearTimeout(addedTimer);
      addedTimer = setTimeout(function () {
        state.added = false;
        render();
      }, 2200);
    }

    function closeSheet() {
      state.upsellOpen = false;
      setHidden(sheet, true);
      if (sheetTeardown) sheetTeardown();
      sheetTeardown = null;
    }
    function openSheet() {
      if (!sheet) return false;
      state.upsellOpen = true;
      showError("");
      render();
      setHidden(sheet, false);
      sheetTeardown = trapDialog(panel || sheet, dismissUpsell);
      var closeBtn = qs(sheet, "[data-braw-" + prefix + "-sheet-close]");
      if (closeBtn) closeBtn.focus();
      else if (panel) panel.focus();
      return true;
    }

    function finalizeCheckout(lines) {
      if (checkoutInFlight) return;
      checkoutInFlight = true;
      upsellShownInFlight = false;
      state.pendingCheckout = false;
      state.adding = true;
      showError("");
      render();
      addLinesAndCheckout(lines)
        .then(function (started) {
          state.adding = false;
          if (started) {
            setAdded();
            closeSheet();
          } else {
            checkoutInFlight = false;
          }
          render();
        })
        .catch(function () {
          checkoutInFlight = false;
          state.adding = false;
          showError(CART_ERROR);
          render();
        });
    }

    function addToCart() {
      var cur = current();
      if (!cur.available || !(cur.variantId > 0)) return;
      if (!sheet || explorerColors.length === 0) {
        finalizeCheckout([{ id: cur.variantId, quantity: 1 }]);
        return;
      }
      if (state.pendingCheckout) {
        finalizeCheckout([{ id: cur.variantId, quantity: 1 }]);
        return;
      }
      if (upsellShownInFlight) return;
      upsellShownInFlight = true;
      if (!openSheet()) upsellShownInFlight = false;
    }

    function confirmAddExplorer() {
      var ec = explorerColor();
      var cur = current();
      if (!ec || !(ec.variantId > 0)) return;
      finalizeCheckout([
        { id: ec.variantId, quantity: 1 },
        { id: cur.variantId, quantity: 1 },
      ]);
    }
    function confirmUpgradeToKit() {
      var kv = kitVariant();
      if (!kv) return;
      finalizeCheckout([{ id: kv.id, quantity: 1 }]);
    }
    function declineUpsell() {
      var cur = current();
      finalizeCheckout([{ id: cur.variantId, quantity: 1 }]);
    }
    function dismissUpsell() {
      closeSheet();
      state.pendingCheckout = true;
      upsellShownInFlight = false;
      render();
    }

    doc.addEventListener("click", function (e) {
      var actionEl = e.target.closest("[data-braw-" + prefix + "-action]");
      if (actionEl) {
        var action = actionEl.getAttribute("data-braw-" + prefix + "-action");
        if (action === "add-to-cart") addToCart();
        else if (action === "add-explorer") confirmAddExplorer();
        else if (action === "upgrade-kit") confirmUpgradeToKit();
        else if (action === "decline") declineUpsell();
        return;
      }
      var chip = e.target.closest("[data-braw-" + prefix + "-explorer-color]");
      if (chip) {
        state.explorerColorId = chip.getAttribute("data-braw-" + prefix + "-explorer-color");
        state.pendingCheckout = false;
        render();
        return;
      }
      if (sheet && !sheet.hasAttribute("hidden")) {
        if (
          e.target.closest("[data-braw-" + prefix + "-sheet-close]") ||
          e.target.closest("[data-braw-" + prefix + "-sheet-backdrop]")
        ) {
          dismissUpsell();
          return;
        }
      }
      if (cfg.onClick) cfg.onClick(e, state, render);
    });

    stickyBarController(bar, cfg.heroBuyId);
    render();
    return { state: state, render: render };
  }

  /* ---------------- WEE Pump page ---------------- */
  (function initPump() {
    if (!doc.getElementById("braw-pump-hero")) return;
    initGallery(qs(doc.getElementById("braw-pump-hero"), "[data-braw-gallery]"));
    accessoryPage({
      prefix: "pump",
      heroId: "braw-pump-hero",
      heroBuyId: "braw-pump-buy-button",
      sheetId: "braw-pump-upsell-sheet",
      stickyId: "braw-pump-sticky-bar",
      current: function (state, data) {
        return {
          variantId: Number(data.variantId) || 0,
          price: Number(data.price) || 0,
          compareAt: data.compareAt == null ? null : Number(data.compareAt),
          available: data.available !== false,
        };
      },
      sheetTitle: function (cur) {
        return "Added: The WEE Pump, " + formatPrice(cur.price);
      },
    });
  })();

  /* ---------------- Wash Bag page ---------------- */
  (function initWashBag() {
    var hero = doc.getElementById("braw-washbag-hero");
    if (!hero) return;
    var galleries = {};
    qsa(hero, "[data-braw-washbag-gallery]").forEach(function (g) {
      galleries[g.getAttribute("data-braw-washbag-gallery")] = initGallery(g);
    });

    function colorFor(state, data) {
      var list = Array.isArray(data.colors) ? data.colors : [];
      return (
        list.filter(function (c) {
          return c.id === state.colorId;
        })[0] ||
        list[0] || { id: "black-orange", label: "Black + Orange", variantId: 0, price: 2199, compareAt: 2999, available: true }
      );
    }

    accessoryPage({
      prefix: "washbag",
      heroId: "braw-washbag-hero",
      heroBuyId: "braw-washbag-buy-button",
      sheetId: "braw-washbag-upsell-sheet",
      stickyId: "braw-washbag-sticky-bar",
      initialColorId: "black-orange",
      fixedExplorerColorId: "black-orange",
      washBagLabelForKit: function (state) {
        return WASH_BAG_LABELS[state.colorId] || WASH_BAG_LABELS["black-orange"];
      },
      current: function (state, data) {
        var c = colorFor(state, data);
        return {
          variantId: Number(c.variantId) || 0,
          price: Number(c.price) || 0,
          compareAt: c.compareAt == null ? null : Number(c.compareAt),
          available: c.available !== false,
          label: c.label,
          id: c.id,
        };
      },
      sheetTitle: function (cur) {
        return "Added: WEE BRAWs Wash Bag, " + formatPrice(cur.price);
      },
      render: function (state, data, cur) {
        setText(qs(hero, "[data-braw-washbag-label]"), cur.label || "");
        qsa(hero, "[data-braw-washbag-color]").forEach(function (sw) {
          sw.setAttribute("aria-pressed", sw.getAttribute("data-braw-washbag-color") === cur.id ? "true" : "false");
        });
        qsa(doc, "[data-braw-washbag-gallery]").forEach(function (g) {
          setHidden(g, g.getAttribute("data-braw-washbag-gallery") !== cur.id);
        });
        qsa(doc, "[data-braw-washbag-feature]").forEach(function (g) {
          setHidden(g, g.getAttribute("data-braw-washbag-feature") !== cur.id);
        });
        qsa(doc, "[data-braw-washbag-sheet-img]").forEach(function (img) {
          setHidden(img, img.getAttribute("data-braw-washbag-sheet-img") !== cur.id);
        });
      },
      onClick: function (e, state, render) {
        var sw = e.target.closest("[data-braw-washbag-color]");
        if (!sw || sw.disabled) return;
        var id = sw.getAttribute("data-braw-washbag-color");
        if (id === state.colorId) return;
        state.colorId = id;
        state.pendingCheckout = false;
        // Gallery resets to the first image on a colour change (WashBagHero.tsx).
        if (galleries[id]) galleries[id].select(0);
        render();
      },
    });
  })();
})();
